import React, { useEffect, useMemo, useState } from "react";
import dagre from "dagre";
import ReactFlow, {
	Background,
	Controls,
	MiniMap,
	Position,
	Handle,
} from "reactflow";
import "reactflow/dist/style.css";
import {
	Brain,
	Search,
	FileText,
	FileSearch,
	CheckCircle2,
	ChevronRight,
	ChevronDown,
	Maximize2,
	Minimize2,
} from "lucide-react";
import { ProviderIcon } from "../../shared/ProviderIcon";
import {
	STEP_LABEL,
	STATUS_COLORS,
	analysisEngineColor,
	analysisEngineLabel,
} from "./fanoutUtils";

const SPINE_COLOR = "var(--monitoring-indigo, #4f46e5)";
const COLLAPSE_THRESHOLD = 6;

const DIMS = {
	root: { w: 280, h: 92 },
	provider: { w: 190, h: 78 },
	reasoning: { w: 220, h: 48 },
	search: { w: 300, h: 118 },
	open_page: { w: 240, h: 76 },
	find_in_page: { w: 240, h: 88 },
	answer: { w: 260, h: 136 },
	group: { w: 360, h: 56 },
	source: { w: 190, h: 58 },
};

function mindmapLeafNodeStyle(width, height, extra = {}) {
	return {
		width: width ?? "100%",
		height: height ?? "100%",
		boxSizing: "border-box",
		...extra,
	};
}

function estimateSearchNodeHeight(queryText, hasBadges) {
	const STEP_HEAD = 22;
	const PADDING_Y = 20;
	const PHRASE_LIST_TOP = 4;
	const LINE_HEIGHT = 18;
	const ITEM_GAP = 2;
	const CHARS_PER_LINE = 34;
	const BADGES = hasBadges ? 30 : 0;
	const BUFFER = 20;

	const phrases = (queryText || "").split(" | ").filter(Boolean);
	if (!phrases.length) {
		return DIMS.search.h;
	}

	const phraseBlockHeight = phrases.reduce((sum, phrase) => {
		const wrappedLines = Math.max(1, Math.ceil(phrase.length / CHARS_PER_LINE));
		return sum + wrappedLines * LINE_HEIGHT + ITEM_GAP;
	}, 0);

	return Math.max(
		DIMS.search.h,
		PADDING_Y + STEP_HEAD + PHRASE_LIST_TOP + phraseBlockHeight + BADGES + BUFFER
	);
}

const STEP_ICON = {
	reasoning: Brain,
	search: Search,
	open_page: FileText,
	find_in_page: FileSearch,
	answer: CheckCircle2,
};

function RootNode({ data }) {
	return (
		<div
			className="monitoring__mindmap-node monitoring__mindmap-node--root"
			style={{ borderColor: SPINE_COLOR }}
		>
			<div className="monitoring__mindmap-node-kicker">Prompt</div>
			<div className="monitoring__mindmap-node-prompt">{data.prompt}</div>
			<Handle type="source" position={Position.Right} />
		</div>
	);
}

function ProviderNode({ data }) {
	const color = analysisEngineColor(data.provider);
	return (
		<div
			className="monitoring__mindmap-node monitoring__mindmap-node--provider"
			style={{ borderColor: color }}
		>
			<Handle type="target" position={Position.Left} />
			<div className="monitoring__mindmap-provider-head" style={{ color }}>
				<ProviderIcon provider={data.provider} size={14} />
				{analysisEngineLabel(data.provider)}
			</div>
			<div className="monitoring__mindmap-provider-model">{data.model}</div>
			{data.error ? (
				<div className="monitoring__mindmap-provider-error">Error</div>
			) : (
				<div className="monitoring__mindmap-provider-tokens">
					{data.inputTokens + data.outputTokens} tokens
				</div>
			)}
			<Handle type="source" position={Position.Right} />
		</div>
	);
}

function StepNode({ data }) {
	const Icon = STEP_ICON[data.actionType] || Search;
	const isSearch = data.actionType === "search";
	const phrases = isSearch
		? (data.queryText || "").split(" | ").filter(Boolean)
		: [];

	return (
		<div
			className={`monitoring__mindmap-node monitoring__mindmap-node--step${
				isSearch ? " monitoring__mindmap-node--step-wide" : ""
			}`}
			style={
				data.layoutHeight
					? { minHeight: data.layoutHeight, boxSizing: "border-box" }
					: undefined
			}
		>
			<Handle type="target" position={Position.Left} />
			<div className="monitoring__mindmap-step-head">
				<Icon size={14} aria-hidden />
				{STEP_LABEL[data.actionType] || data.actionType}
			</div>
			{isSearch ? (
				phrases.length > 0 && (
					<ul className="monitoring__mindmap-step-phrases">
						{phrases.map((p, i) => (
							<li key={i}>{p}</li>
						))}
					</ul>
				)
			) : (
				data.queryText && (
					<div className="monitoring__mindmap-step-text">{data.queryText}</div>
				)
			)}
			{data.badges.length > 0 && (
				<div className="monitoring__mindmap-step-badges">
					{data.badges.map((b, i) => (
						<span
							key={i}
							className="monitoring__mindmap-step-badge"
							style={{ backgroundColor: `${b.color}22`, color: b.color }}
						>
							{b.label}
						</span>
					))}
				</div>
			)}
			<Handle type="source" position={Position.Right} />
		</div>
	);
}

function GroupNode({ data, width, height }) {
	return (
		<div
			role={data.collapsible ? "button" : undefined}
			tabIndex={data.collapsible ? 0 : undefined}
			onClick={data.collapsible ? data.onToggle : undefined}
			onKeyDown={
				data.collapsible
					? (e) => {
							if (e.key === "Enter" || e.key === " ") data.onToggle();
						}
					: undefined
			}
			className={`monitoring__mindmap-node monitoring__mindmap-node--group${
				data.collapsible ? " monitoring__mindmap-node--clickable" : ""
			}`}
			style={mindmapLeafNodeStyle(width, height, { borderColor: data.color })}
		>
			<Handle type="target" position={Position.Left} />
			<div className="monitoring__mindmap-group-head" style={{ color: data.color }}>
				{data.collapsible &&
					(data.collapsed ? (
						<ChevronRight size={12} aria-hidden />
					) : (
						<ChevronDown size={12} aria-hidden />
					))}
				<span className="monitoring__mindmap-group-label">
					{data.label} ({data.count})
				</span>
			</div>
			<Handle type="source" position={Position.Right} />
		</div>
	);
}

function SourceNode({ data, width, height }) {
	return (
		<div
			className={`monitoring__mindmap-node monitoring__mindmap-node--source${
				data.dim ? " monitoring__mindmap-node--dim" : ""
			}`}
			style={mindmapLeafNodeStyle(width, height, { borderLeftColor: data.color })}
			title={data.url}
		>
			<Handle type="target" position={Position.Left} />
			<div className="monitoring__mindmap-source-primary">{data.primary}</div>
			{data.secondary && (
				<div className="monitoring__mindmap-source-secondary">{data.secondary}</div>
			)}
		</div>
	);
}

const NODE_TYPES = {
	root: RootNode,
	provider: ProviderNode,
	step: StepNode,
	group: GroupNode,
	source: SourceNode,
};

function buildGraph(run, expandedGroups, onToggleGroup) {
	const nodes = [];
	const edges = [];
	const g = new dagre.graphlib.Graph();
	const providerCount = (run.analyses || []).length;
	g.setGraph({
		rankdir: "LR",
		nodesep: Math.max(56, providerCount * 20),
		ranksep: 64,
	});
	g.setDefaultEdgeLabel(() => ({}));

	const addNode = (id, kind, type, data, heightOverride) => {
		const dim = DIMS[kind] || DIMS.search;
		const height = heightOverride ?? dim.h;
		g.setNode(id, { width: dim.w, height });
		nodes.push({
			id,
			type,
			data,
			position: { x: 0, y: 0 },
			width: dim.w,
			height,
		});
	};

	const addEdge = (
		source,
		target,
		color = SPINE_COLOR,
		dashed = false,
		type = "default"
	) => {
		g.setEdge(source, target);
		edges.push({
			id: `${source}->${target}`,
			source,
			target,
			type,
			style: {
				stroke: color,
				strokeWidth: 1.5,
				...(dashed ? { strokeDasharray: "4 3" } : {}),
			},
		});
	};

	const rootId = "root";
	addNode(rootId, "root", "root", { prompt: run.prompt });

	const groupLabel = (t) =>
		t === "fetched" ? "Researched" : t.charAt(0).toUpperCase() + t.slice(1);

	(run.analyses || []).forEach((a) => {
		const providerId = `provider-${a.id}`;
		addNode(providerId, "provider", "provider", {
			provider: a.provider,
			model: a.model,
			error: a.error,
			inputTokens: a.input_tokens,
			outputTokens: a.output_tokens,
		});
		addEdge(rootId, providerId, SPINE_COLOR, false, "straight");

		const sortedQueries = [...(a.queries || [])].sort(
			(x, y) => x.seq_order - y.seq_order
		);
		let prevId = providerId;

		sortedQueries.forEach((q) => {
			const stepId = `step-${a.id}-${q.id}`;
			const qSources = (a.sources || []).filter((s) => s.query_id === q.id);
			const counts = { cited: 0, mentioned: 0, fetched: 0 };
			qSources.forEach((s) => {
				if (counts[s.citation_type] != null) counts[s.citation_type]++;
			});
			const badges = (["cited", "mentioned", "fetched"])
				.filter((t) => counts[t] > 0)
				.map((t) => ({
					label: `${groupLabel(t)} ${counts[t]}`,
					color: STATUS_COLORS[t],
				}));

			const stepHeight =
				q.action_type === "search"
					? estimateSearchNodeHeight(q.query_text || "", badges.length > 0)
					: undefined;
			addNode(
				stepId,
				q.action_type,
				"step",
				{
					actionType: q.action_type,
					queryText: q.query_text || "",
					badges,
					layoutHeight: stepHeight,
				},
				stepHeight
			);
			addEdge(prevId, stepId);
			prevId = stepId;

			const byType = { cited: [], mentioned: [], fetched: [] };
			qSources.forEach((s) => {
				if (byType[s.citation_type]) byType[s.citation_type].push(s);
			});

			(["cited", "mentioned", "fetched"]).forEach((groupType) => {
				const list = byType[groupType];
				if (!list.length) return;
				const groupId = `group-${a.id}-${q.id}-${groupType}`;
				const collapsible = list.length > COLLAPSE_THRESHOLD;
				const collapsed = collapsible && !expandedGroups.has(groupId);
				addNode(groupId, "group", "group", {
					label: groupLabel(groupType),
					count: list.length,
					collapsed,
					collapsible,
					color: STATUS_COLORS[groupType],
					onToggle: () => onToggleGroup(groupId),
				});
				addEdge(stepId, groupId, STATUS_COLORS[groupType], groupType === "fetched");

				if (!collapsed) {
					list.forEach((s) => {
						const sourceId = `source-${s.id}`;
						const primary =
							groupType === "mentioned"
								? s.title || s.domain || s.url
								: s.domain || s.title || s.url;
						const secondary = groupType === "mentioned" ? s.domain : s.title;
						addNode(sourceId, "source", "source", {
							primary,
							secondary,
							url: s.url,
							color: STATUS_COLORS[groupType],
							dim: groupType === "fetched",
						});
						addEdge(
							groupId,
							sourceId,
							STATUS_COLORS[groupType],
							groupType === "fetched"
						);
					});
				}
			});
		});
	});

	dagre.layout(g);
	nodes.forEach((n) => {
		const pos = g.node(n.id);
		n.position = {
			x: pos.x - (n.width ?? 0) / 2,
			y: pos.y - (n.height ?? 0) / 2,
		};
	});

	const rootNode = nodes.find((n) => n.id === rootId);
	const providerNodes = nodes.filter((n) => n.type === "provider");
	if (rootNode && providerNodes.length) {
		const minY = Math.min(...providerNodes.map((n) => n.position.y));
		const maxY = Math.max(
			...providerNodes.map((n) => n.position.y + (n.height ?? 0))
		);
		rootNode.position = {
			...rootNode.position,
			y: (minY + maxY) / 2 - (rootNode.height ?? 0) / 2,
		};
	}

	return { nodes, edges };
}

export default function FanoutGraphView({
	run,
	fullscreen,
	onFullscreenChange,
	headerExtra,
}) {
	const [expandedGroups, setExpandedGroups] = useState(() => new Set());

	const toggleGroup = (id) => {
		setExpandedGroups((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const { nodes, edges } = useMemo(
		() => buildGraph(run, expandedGroups, toggleGroup),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[run, expandedGroups]
	);

	useEffect(() => {
		if (!fullscreen) return undefined;
		const onKey = (e) => {
			if (e.key === "Escape") onFullscreenChange(false);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [fullscreen, onFullscreenChange]);

	const graph = (
		<ReactFlow
			nodes={nodes}
			edges={edges}
			nodeTypes={NODE_TYPES}
			fitView
			fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
			minZoom={0.05}
			proOptions={{ hideAttribution: true }}
		>
			<Background
				color="var(--monitoring-muted, #6b7280)"
				gap={20}
				size={1}
				style={{ opacity: 0.25 }}
			/>
			<Controls showInteractive={false} />
			<MiniMap
				pannable
				zoomable
				nodeColor={(n) => {
					if (n.type === "root") return "var(--monitoring-indigo, #4f46e5)";
					if (n.type === "provider") return analysisEngineColor(n.data.provider);
					if (n.type === "source") return n.data.color;
					return "#94a3b8";
				}}
			/>
		</ReactFlow>
	);

	if (fullscreen) {
		return (
			<div
				className="monitoring__mindmap-fullscreen-backdrop"
				role="dialog"
				aria-modal="true"
				onClick={() => onFullscreenChange(false)}
			>
				<div
					className="monitoring__mindmap-fullscreen-dialog"
					onClick={(e) => e.stopPropagation()}
				>
					<div className="monitoring__mindmap-fullscreen-head">
						<div className="monitoring__mindmap-fullscreen-head-main">
							<h3 className="monitoring__mindmap-fullscreen-title">Query fan-out</h3>
							{headerExtra}
						</div>
						<button
							type="button"
							onClick={() => onFullscreenChange(false)}
							aria-label="Exit full screen"
							className="monitoring__mindmap-fs-btn"
						>
							<Minimize2 size={14} aria-hidden /> Close
						</button>
					</div>
					<div className="monitoring__mindmap-canvas monitoring__mindmap-canvas--fullscreen">
						{graph}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="monitoring__mindmap-wrap monitoring__fanout-view-panel">
			<button
				type="button"
				onClick={() => onFullscreenChange(true)}
				aria-label="View graph full screen"
				className="monitoring__mindmap-fs-btn monitoring__mindmap-fs-btn--overlay"
			>
				<Maximize2 size={14} aria-hidden /> Full screen
			</button>
			<div className="monitoring__mindmap-canvas">{graph}</div>
		</div>
	);
}
